import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Send, MoreVertical, ArrowLeft, User, X, Check, Ban } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { ChatBubble } from './ChatBubble';
import ChatInputToolbar from './ChatInputToolbar';

const API = '/api';

const ChatThread = ({ conversation, onBack, onConversationUpdate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [convStatus, setConvStatus] = useState(conversation?.status || 'active');

  // Image attachment state
  const [pendingImage, setPendingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const shouldAutoScroll = useRef(true);
  const lastMarkReadRef = useRef(0);

  const other = conversation?.other_user || {};
  const convId = conversation?.id;
  const isRecipient = conversation?.recipient_id === user?.id;
  const isRequest = convStatus === 'request';
  const isClosed = convStatus === 'closed';
  const isActive = convStatus === 'active';
  const canSend = isActive || (isRequest && !isRecipient);

  // Smart scroll
  const isNearBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    if (force || shouldAutoScroll.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleScroll = useCallback(() => {
    shouldAutoScroll.current = isNearBottom();
  }, [isNearBottom]);

  // Persist reaction via API
  const handleReaction = useCallback(async (messageId, reactionType) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/messages/${messageId}/react`,
        { reaction_type: reactionType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const newReactions = res.data.reactions || [];
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, reactions: newReactions } : m)),
      );
    } catch {
      toast.error('Failed to react');
    }
  }, []);

  // Image handlers
  const handleImageSelect = useCallback((file) => {
    setPendingImage(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleImageRemove = useCallback(() => {
    setPendingImage(null);
    setImagePreview(null);
  }, []);

  const uploadImage = useCallback(async (file) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    setImageUploading(true);
    try {
      const res = await axios.post(`${API}/messages/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      return res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
      return null;
    } finally {
      setImageUploading(false);
    }
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(
    async (initial = false) => {
      if (!convId) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/messages/${convId}?limit=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const msgs = Array.isArray(res.data) ? res.data : [];
        let changed = false;
        if (initial) {
          setMessages(msgs);
          setHasOlder(msgs.length >= 30);
          changed = true;
        } else {
          setMessages((prev) => {
            if (
              msgs.length !== prev.length ||
              (msgs.length > 0 && prev.length > 0 && msgs[msgs.length - 1]?.id !== prev[prev.length - 1]?.id)
            ) {
              changed = true;
              return msgs;
            }
            return prev;
          });
        }
        // Only mark-read on initial load or when new messages arrive, debounced to 3s
        const now = Date.now();
        if (changed && now - lastMarkReadRef.current > 3000) {
          lastMarkReadRef.current = now;
          axios.patch(`${API}/messages/mark-read`, JSON.stringify(convId), {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }).catch(() => {});
        }
      } catch {
        if (initial) setMessages([]);
      } finally {
        if (initial) setLoading(false);
      }
    },
    [convId],
  );

  useEffect(() => {
    setLoading(true);
    setConvStatus(conversation?.status || 'active');
    setPendingImage(null);
    setImagePreview(null);
    fetchMessages(true);
  }, [convId, fetchMessages, conversation?.status]);

  useEffect(() => { if (!loading) scrollToBottom(true); }, [loading]); // eslint-disable-line
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!convId || isClosed) return;
    pollRef.current = setInterval(() => fetchMessages(false), 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [convId, isClosed, fetchMessages]);

  const loadOlder = async () => {
    if (!convId || !messages.length || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const token = localStorage.getItem('token');
      const oldest = messages[0];
      const res = await axios.get(`${API}/messages/${convId}?limit=30&before=${oldest.created_at}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const older = Array.isArray(res.data) ? res.data : [];
      if (older.length === 0) { setHasOlder(false); return; }
      setMessages((prev) => [...older, ...prev]);
      setHasOlder(older.length >= 30);
    } catch {
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleSend = async () => {
    const text = inputText.trim();
    const hasImage = !!pendingImage;
    if (!text && !hasImage) return;
    if (sending || !convId) return;

    let imageUrl = null;
    if (hasImage) {
      imageUrl = await uploadImage(pendingImage);
      if (!imageUrl) return;
    }

    const tempMsg = {
      id: `temp-${Date.now()}`,
      sender_id: user.id,
      content: text,
      image_url: imageUrl,
      created_at: new Date().toISOString(),
      reactions: [],
      _sending: true,
    };
    setMessages((prev) => [...prev, tempMsg]);
    setInputText('');
    setPendingImage(null);
    setImagePreview(null);
    setSending(true);
    shouldAutoScroll.current = true;

    try {
      const token = localStorage.getItem('token');
      const payload = { conversation_id: convId, content: text };
      if (imageUrl) payload.image_url = imageUrl;
      const res = await axios.post(`${API}/messages/send`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === tempMsg.id ? { ...res.data, reactions: [], _sending: false } : m)),
      );
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleAccept = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/conversations/${convId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConvStatus('active');
      toast.success('Request accepted \u2014 you can now chat!');
      onConversationUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept request');
    }
  };

  const handleEnd = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/conversations/${convId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConvStatus('closed');
      toast.success('Conversation ended');
      onConversationUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end conversation');
    }
  };

  const handleNewRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/conversations/with`, { other_user_id: other.id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('New message request sent');
      navigate(`/messages?conversation=${res.data.conversation_id}`);
      onConversationUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Compute last outgoing message ID for read receipt (must be before early return)
  const lastOutgoingMsgId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender_id === user?.id) return messages[i].id;
    }
    return null;
  }, [messages, user?.id]);

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-zinc-950" data-testid="chat-empty-state">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
            <Send className="w-7 h-7 text-zinc-600" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-1">Your Messages</h3>
          <p className="text-sm text-zinc-500">Select a conversation to start chatting</p>
        </div>
      </div>
    );
  }

  const displayName = other.display_name || other.handle || 'Creator';
  const avatarLetter = displayName[0].toUpperCase();

  return (
    <div className="flex-1 flex flex-col bg-zinc-950 min-w-0" data-testid="chat-thread">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-900/50 flex-shrink-0">
        <button onClick={onBack} className="md:hidden p-1 text-zinc-400 hover:text-white" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Avatar className="h-9 w-9">
          {other.avatar_url && <AvatarImage src={other.avatar_url} alt={displayName} />}
          <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-sm font-bold">{avatarLetter}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{displayName}</p>
          {other.handle && <p className="text-[10px] text-zinc-500">@{other.handle}</p>}
        </div>
        <div className="flex items-center gap-1">
          {other.handle && (
            <Button variant="ghost" size="sm" onClick={() => navigate(`/u/${other.handle}`)} className="text-xs text-zinc-400 hover:text-white h-8" data-testid="view-profile-btn">
              <User className="w-3.5 h-3.5 mr-1" />Profile
            </Button>
          )}
          {(isActive || isRequest) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 text-zinc-400 hover:text-white rounded-md hover:bg-zinc-800" data-testid="chat-menu-btn">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                <DropdownMenuItem onClick={handleEnd} className="text-red-400 focus:text-red-300 cursor-pointer" data-testid="end-conversation-btn">
                  <Ban className="w-4 h-4 mr-2" />End Conversation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Request banner */}
      {isRequest && isRecipient && (
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800" data-testid="request-banner">
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-white">{displayName}</span> wants to message you
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAccept} className="bg-orange-500 hover:bg-orange-600 h-8 text-xs" data-testid="accept-request-btn">
              <Check className="w-3.5 h-3.5 mr-1" />Accept
            </Button>
            <Button size="sm" variant="ghost" onClick={handleEnd} className="text-zinc-400 hover:text-red-400 h-8 text-xs" data-testid="ignore-request-btn">
              <X className="w-3.5 h-3.5 mr-1" />Ignore
            </Button>
          </div>
        </div>
      )}

      {/* Closed banner */}
      {isClosed && (
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800" data-testid="closed-banner">
          <p className="text-sm text-zinc-400">This conversation has been closed</p>
          <Button size="sm" onClick={handleNewRequest} className="bg-orange-500 hover:bg-orange-600 h-8 text-xs" data-testid="new-request-btn">
            Start New Request
          </Button>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-3" onScroll={handleScroll} data-testid="messages-area">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
          </div>
        ) : (
          <>
            {hasOlder && messages.length >= 30 && (
              <div className="text-center py-2">
                <button onClick={loadOlder} disabled={loadingOlder} className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="load-older-btn">
                  {loadingOlder ? 'Loading...' : 'Load older messages'}
                </button>
              </div>
            )}
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <Avatar className="h-16 w-16 mb-3">
                  {other.avatar_url && <AvatarImage src={other.avatar_url} alt={displayName} />}
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-red-500 text-white text-xl font-bold">{avatarLetter}</AvatarFallback>
                </Avatar>
                <p className="text-sm text-zinc-400">Start a conversation with</p>
                <p className="text-sm font-semibold text-white">{displayName}</p>
              </div>
            )}
            {messages.map((msg, i) => {
              const isMine = msg.sender_id === user?.id;
              const prevMsg = messages[i - 1];
              const compact = prevMsg && prevMsg.sender_id === msg.sender_id;
              return (
                <ChatBubble
                  key={msg.id}
                  message={msg}
                  isMine={isMine}
                  senderAvatar={!isMine ? other.avatar_url : null}
                  senderName={!isMine ? displayName : null}
                  compact={compact}
                  reactions={msg.reactions || []}
                  onReact={handleReaction}
                  isLastOutgoing={msg.id === lastOutgoingMsgId}
                />
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Active input toolbar */}
      {canSend && (
        <ChatInputToolbar
          inputText={inputText}
          onInputChange={setInputText}
          onSend={handleSend}
          onKeyDown={handleKeyDown}
          sending={sending}
          disabled={false}
          inputRef={inputRef}
          onImageSelect={handleImageSelect}
          imagePreview={imagePreview}
          onImageRemove={handleImageRemove}
          imageUploading={imageUploading}
          data-testid-input="chat-input"
          data-testid-send="chat-send-btn"
        />
      )}

      {isRequest && isRecipient && (
        <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/30 text-center" data-testid="request-pending-input">
          <p className="text-xs text-zinc-500">Accept the request to start chatting</p>
        </div>
      )}

      {isClosed && (
        <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/30 text-center" data-testid="closed-input">
          <p className="text-xs text-zinc-500">This conversation is closed</p>
        </div>
      )}
    </div>
  );
};

export default ChatThread;
